import {
  AccountUserRolesListResponse,
  SubAccountsRequest,
  SubAccountsResponse,
  UpdateAccountUserRoleRequest,
  UpdateAccountUserRoleResponse,
} from 'vopay';

export type PropagateRoleUpdateResult = {
  numTotal: number;
  numUpdated: number;
  skipped: string[];
  failed: string[];
};

type Deps = {
  fetchSubAccountsAsync: (params: SubAccountsRequest) => Promise<SubAccountsResponse>;
  fetchAccountUserRolesWithBearer: (bearer: string) => Promise<AccountUserRolesListResponse>;
  updateUserRole: (params: UpdateAccountUserRoleRequest, bearer?: string) => Promise<UpdateAccountUserRoleResponse>;
};

type RoleUpdatePayload = Omit<UpdateAccountUserRoleRequest, 'RoleID'> & { Name: string };

type PropagateOptions = {
  /**
   * Name to look up against each sub-account's existing roles. Pass the role's
   * pre-edit name when the user is renaming, otherwise sub-accounts will get
   * skipped because we'd be searching for a name that doesn't exist yet.
   */
  lookupName: string;
};

/**
 * Fan-out helper for editing a role across sub-accounts.
 *
 * Each sub-account has its own RoleID for the same logical role, so we have to
 * look up the role by Name on the sub-account first, then call update with
 * that sub-account's RoleID + bearer.
 *
 * Sub-accounts with no matching role by name are reported as `skipped` — we
 * don't create the role there, since this is an edit, not a propagate-create.
 *
 * A failure of the outer sub-accounts fetch throws so the caller doesn't
 * mistake it for "zero sub-accounts to update".
 */
export async function propagateRoleUpdateToSubAccounts(
  payload: RoleUpdatePayload,
  { fetchSubAccountsAsync, fetchAccountUserRolesWithBearer, updateUserRole }: Deps,
  { lookupName }: PropagateOptions,
): Promise<PropagateRoleUpdateResult> {
  const subAccountsRes = await fetchSubAccountsAsync({ ShowToken: true, ActiveOnly: true });

  if (!subAccountsRes.Success) {
    throw new Error(subAccountsRes.ErrorMessage || 'Failed to fetch sub-accounts');
  }

  const subAccounts = subAccountsRes.Subaccounts;

  const results = await Promise.all(
    subAccounts.map(async (subAccount) => {
      const label = subAccount.AccountName || subAccount.AccountID;

      try {
        const rolesRes = await fetchAccountUserRolesWithBearer(subAccount.Token);

        if (!rolesRes.Success) {
          return { kind: 'failed', label };
        }

        const matchedRole = rolesRes.Roles.find((role) => role.Name === lookupName);

        if (!matchedRole) {
          return { kind: 'skipped', label };
        }

        await updateUserRole({ ...payload, RoleID: matchedRole.RoleID }, subAccount.Token);

        return { kind: 'updated', label };
      } catch (error) {
        console.error(`Failed to update role "${lookupName}" in sub-account ${label}:`, error);

        return { kind: 'failed', label };
      }
    }),
  );

  return {
    numTotal: subAccounts.length,
    numUpdated: results.filter((r) => r.kind === 'updated').length,
    skipped: results.filter((r) => r.kind === 'skipped').map((r) => r.label),
    failed: results.filter((r) => r.kind === 'failed').map((r) => r.label),
  };
}
